"use client";

import { useEffect, useMemo, useState } from "react";
import etStyles from "@/app/(components)/generics/EditableText/EditableText.module.css";
import IconCheck from "@/app/(components)/generics/Icons/IconCheck";
import IconClose from "@/app/(components)/generics/Icons/IconClose";
import IconEdit from "@/app/(components)/generics/Icons/IconEdit";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import IconTrash from "@/app/(components)/generics/Icons/IconTrash";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { Globals } from "@/app/vars/Globals";
import styles from "./EditableTimeline.module.css";

type TimelineItem = {
  time: string;
  text: string;
};

type Props = {
  items: TimelineItem[];
  onSave: (items: TimelineItem[]) => void;
  isPlaceholder?: boolean;
  disabled?: boolean;
  lang?: string;
};

export default function EditableTimeline({
  items,
  onSave,
  isPlaceholder,
  disabled = false,
}: Props) {
  const T = useT();
  const normalizedItems = useMemo(() => {
    if (Array.isArray(items)) {
      return items.filter((item) => item.time?.trim() || item.text?.trim());
    }
    return [];
  }, [items]);

  const [localItems, setLocalItems] = useState<TimelineItem[]>(normalizedItems);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [addingRow, setAddingRow] = useState<{ time: string; text: string } | null>(null);

  useEffect(() => {
    setLocalItems(normalizedItems);
  }, [normalizedItems]);

  const handleUpdate = (index: number, field: "time" | "text", val: string) => {
    const next = [...localItems];
    next[index] = { ...next[index], [field]: val };
    setLocalItems(next);
  };

  const _handleDelete = (index: number) => {
    const next = localItems.filter((_, i) => i !== index);
    setLocalItems(next);
  };

  const handleInstantDelete = (index: number) => {
    const next = localItems.filter((_, i) => i !== index);
    setLocalItems(next);
    onSave(next);
  };

  const handleAdd = () => {
    setAddingRow({ time: "", text: "" });
  };

  const handleConfirmAdd = () => {
    if (addingRow) {
      const time = addingRow.time.trim();
      const text = addingRow.text.trim();
      if (time || text) {
        const next = [...localItems, { time, text }];
        setLocalItems(next);
        onSave(next);
      }
    }
    setAddingRow(null);
  };

  const saveItem = (_index: number) => {
    const trimmed = localItems
      .map((item) => ({ time: item.time.trim(), text: item.text.trim() }))
      .filter((item) => item.time || item.text);
    setLocalItems(trimmed);
    onSave(trimmed);
    setEditingIndex(null);
  };

  const cancelEdit = () => {
    // Revert changes to the item
    setLocalItems(normalizedItems);
    setEditingIndex(null);
  };

  if (isPlaceholder) return null;

  return (
    <div className={styles.root}>
      <div className={styles.displayRow}>
        <ul className={styles.timelineList} style={{ flex: 1 }}>
          {localItems.map((routine, idx) => {
            if (editingIndex === idx) {
              return (
                <li className={styles.timelineItem} key={idx}>
                  <div className={styles.timeInputWrapper}>
                    <input
                      type="text"
                      value={routine.time}
                      onChange={(e) => handleUpdate(idx, "time", e.target.value)}
                      placeholder={T(TKey.TIME_MOMENT) as string}
                      className={etStyles.input}
                    />
                  </div>
                  <div className={`${etStyles.editContainer} ${styles.textInputWrapper}`}>
                    <textarea
                      value={routine.text}
                      onChange={(e) => handleUpdate(idx, "text", e.target.value)}
                      placeholder={T(TKey.NEW_ACTIVITY) as string}
                      className={etStyles.textarea}
                      rows={3}
                    />
                    <div className={etStyles.actions}>
                      <button
                        type="button"
                        className={`${etStyles.actionBtn} ${etStyles.saveBtn}`}
                        onClick={() => saveItem(idx)}
                        title={T(TKey.VALIDATE) as string}
                      >
                        <IconCheck />
                      </button>
                      <button
                        type="button"
                        className={`${etStyles.actionBtn} ${etStyles.cancelBtn}`}
                        onClick={cancelEdit}
                        title={T(TKey.CANCEL) as string}
                      >
                        <IconClose />
                      </button>
                    </div>
                  </div>
                </li>
              );
            }

            return (
              <li className={styles.timelineItemDisplay} key={idx}>
                <div className={styles.timelineItemDisplayRow}>
                  <div className={styles.timelineItemContent}>
                    <div className={styles.timelineTime}>{routine.time}</div>
                    <div className={styles.timelineText}>{routine.text}</div>
                  </div>
                  {!disabled && (
                    <div className={styles.itemActionsHover}>
                      <button
                        type="button"
                        className={styles.editBtn}
                        onClick={() => setEditingIndex(idx)}
                        title={T(TKey.EDIT) as string}
                      >
                        <IconEdit />
                      </button>
                      <button
                        type="button"
                        className={styles.deleteBtn}
                        onClick={() => handleInstantDelete(idx)}
                        title={T(TKey.DELETE) as string}
                      >
                        <IconTrash />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
          {addingRow && (
            <li className={styles.timelineItem} key="add">
              <div className={styles.timeInputWrapper}>
                <input
                  type="text"
                  value={addingRow.time}
                  onChange={(e) => setAddingRow({ ...addingRow, time: e.target.value })}
                  placeholder={T(TKey.TIME_MOMENT) as string}
                  className={etStyles.input}
                />
              </div>
              <div className={`${etStyles.editContainer} ${styles.textInputWrapper}`}>
                <textarea
                  value={addingRow.text}
                  onChange={(e) => setAddingRow({ ...addingRow, text: e.target.value })}
                  placeholder={T(TKey.NEW_ACTIVITY) as string}
                  className={etStyles.textarea}
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleConfirmAdd();
                    }
                    if (e.key === "Escape") {
                      setAddingRow(null);
                    }
                  }}
                />
                <div className={etStyles.actions}>
                  <button
                    type="button"
                    className={`${etStyles.actionBtn} ${etStyles.saveBtn}`}
                    onClick={handleConfirmAdd}
                    disabled={!addingRow.time.trim() && !addingRow.text.trim()}
                    title={T(TKey.VALIDATE) as string}
                  >
                    <IconCheck />
                  </button>
                  <button
                    type="button"
                    className={`${etStyles.actionBtn} ${etStyles.cancelBtn}`}
                    onClick={() => setAddingRow(null)}
                    title={T(TKey.CANCEL) as string}
                  >
                    <IconClose />
                  </button>
                </div>
              </div>
            </li>
          )}
        </ul>
      </div>
      {!disabled && !addingRow && normalizedItems.length < Globals.MAX_AVATAR_TIMELINE_ITEMS && (
        <div className={styles.addAction}>
          <button type="button" onClick={handleAdd} className={styles.addBtnList}>
            <IconPlus />
            <span>{T(TKey.ADD_ITEM)}</span>
          </button>
        </div>
      )}
    </div>
  );
}
